import { Dialog, Select } from './FarChat/ChatFeedbackOverlays';
import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  fetchSignupUnits,
  submitSelfSignup,
  SelfSignupStatus,
} from '../api/signupFunctions';

interface SignupFormProps {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
}

const OTHER_OPTION = '__other__';

const CATEGORY_OPTIONS = [
  'Contracting Officer/Specialist',
  'Civil Engineering',
  'Resource Advisor',
  'DCMA',
];

const HEARD_ABOUT_OPTIONS = [
  'Facebook',
  'WIFCON',
  'Word of mouth',
  'Squadron leadership',
  'Other',
];

const STATUS_MESSAGES: Record<SelfSignupStatus, string> = {
  pending: "Request received — we'll email you when it's reviewed.",
  duplicate_pending: 'You already have a pending request.',
  already_whitelisted: 'This email is already authorized — just sign in.',
  approved:
    "You're all set — your Waypoint account is active. Check your email for confirmation, then sign in with your address.",
};

const validateEmail = (e: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// All form control boxes (TextFields, and the Unit / "heard about" Selects)
// must close to the SAME pixel height. A <TextField> sizes its inner input
// directly, but a <FormControl><Select/></FormControl> does not inherit that —
// its height is governed by its own `size`. We therefore give every control the
// identical explicit `size` AND pin the outlined-root height with a shared `sx`
// so the boxes line up pixel-for-pixel regardless of input vs. select internals.
const CONTROL_SIZE = 'small' as const;
const CONTROL_HEIGHT = 36; // MUI outlined medium default
const controlSx = {
  '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
} as const;

const SignupForm: React.FC<SignupFormProps> = ({ open, onClose, initialEmail }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Unit picker (non-mutating): a Select of canonical units plus "Other…".
  const [units, setUnits] = useState<string[]>([]);
  const [unitChoice, setUnitChoice] = useState('');
  const [unitOther, setUnitOther] = useState('');
  // In-dropdown search that filters the unit MenuItems (case-insensitive).
  const [unitSearch, setUnitSearch] = useState('');

  // Category picker (required).
  const [category, setCategory] = useState<string>('');

  // "How did you hear about us?" picker.
  const [heardChoice, setHeardChoice] = useState('');
  const [heardOther, setHeardOther] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // The status returned by submitSelfSignup, once a result exists. Drives the
  // success-vs-warning result presentation. `null` means "not yet submitted".
  const [resultStatus, setResultStatus] = useState<SelfSignupStatus | null>(
    null,
  );

  // Reset form state and prefill the email whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setEmail(initialEmail || '');
      setFirstName('');
      setLastName('');
      setUnitChoice('');
      setUnitOther('');
      setUnitSearch('');
      setCategory('');
      setHeardChoice('');
      setHeardOther('');
      setError('');
      setSuccessMessage('');
      setResultStatus(null);
    }
  }, [open, initialEmail]);

  // Fetch units when the dialog opens (non-mutating — does NOT touch units.json).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchSignupUnits()
      .then((u) => {
        if (!cancelled) setUnits(u);
      })
      .catch(() => {
        // Units are advisory; the user can still pick "Other…" and type one.
        if (!cancelled) setUnits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Resolve the chosen-or-typed unit (plain text).
  const resolvedUnit =
    unitChoice === OTHER_OPTION ? unitOther.trim() : (unitChoice ?? '').trim();

  // Resolve the heard-about value, e.g. "Other: a friend".
  const resolvedHeardAbout =
    heardChoice === 'Other'
      ? heardOther.trim()
        ? `Other: ${heardOther.trim()}`
        : ''
      : heardChoice.trim();

  const emailValid = validateEmail(email.trim());

  const canSubmit =
    !loading &&
    !successMessage &&
    emailValid &&
    !!firstName.trim() &&
    !!lastName.trim() &&
    !!resolvedUnit &&
    !!category &&
    !!resolvedHeardAbout;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      const result = await submitSelfSignup({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        unit: resolvedUnit,
        category,
        heard_about: resolvedHeardAbout,
      });
      setResultStatus(result.status);
      setSuccessMessage(
        STATUS_MESSAGES[result.status] ||
          "Request received — we'll email you when it's reviewed.",
      );
    } catch {
      setError(
        'Something went wrong submitting your request. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6">Create new account</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={1} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Intro is shown only while filling out the form; once a result is
              being displayed (successMessage set) it is hidden. */}
          {!successMessage && (
            <Typography variant="body2" color="textSecondary">
              Request access to Waypoint. We&apos;ll review your request and
              email you when it&apos;s approved.
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {/* A brand-new request (status 'pending') gets the green success
              treatment. An already-authorized email or a duplicate pending
              request gets an attention-getting warning treatment with larger,
              bold text. */}
          {successMessage &&
            (resultStatus === 'pending' || resultStatus === 'approved' ? (
              <Box display="flex" alignItems="center" gap={1.5}>
                <CheckCircleIcon color="success" />
                <Typography variant="body1" color="success.main">
                  {successMessage}
                </Typography>
              </Box>
            ) : (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="inherit" />}
              >
                <Typography variant="h6" fontWeight="bold">
                  {successMessage}
                </Typography>
              </Alert>
            ))}

          {!successMessage && (
            <>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                variant="outlined"
                size={CONTROL_SIZE}
                sx={controlSx}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!email && !emailValid}
                helperText={
                  !!email && !emailValid ? 'Enter a valid email address' : ' '
                }
                inputProps={{ 'data-testid': 'signup-email' }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First Name"
                  fullWidth
                  required
                  variant="outlined"
                  size={CONTROL_SIZE}
                  sx={controlSx}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  inputProps={{ 'data-testid': 'signup-first-name' }}
                />
                <TextField
                  label="Last Name"
                  fullWidth
                  required
                  variant="outlined"
                  size={CONTROL_SIZE}
                  sx={controlSx}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  inputProps={{ 'data-testid': 'signup-last-name' }}
                />
              </Box>

              {/* Unit — non-mutating picker with in-dropdown search. Does NOT call addUnit. */}
              <FormControl
                fullWidth
                required
                variant="outlined"
                size={CONTROL_SIZE}
                sx={controlSx}
              >
                <InputLabel id="signup-unit-label">Unit</InputLabel>
                <Select
                  labelId="signup-unit-label"
                  label="Unit"
                  size={CONTROL_SIZE}
                  value={unitChoice}
                  onChange={(e) => {
                    // The pinned search box is a valueless MenuItem; selecting it
                    // yields `undefined` — ignore that so unitChoice stays a string.
                    const v = e.target.value;
                    if (typeof v === 'string') setUnitChoice(v);
                  }}
                  onClose={() => setUnitSearch('')}
                  data-testid="signup-unit-select"
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 350 } },
                  }}
                >
                  {/* Search box pinned at the top of the open menu; typing here
                      must not select an option or close the menu. */}
                  <MenuItem
                    disableRipple
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ py: 0.5 }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search units…"
                      value={unitSearch}
                      onChange={(e) => setUnitSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      fullWidth
                      inputProps={{ 'data-testid': 'signup-unit-search' }}
                    />
                  </MenuItem>
                  {units
                    .filter((u) =>
                      u.toLowerCase().includes(unitSearch.toLowerCase()),
                    )
                    .map((u) => (
                      <MenuItem key={u} value={u}>
                        {u}
                      </MenuItem>
                    ))}
                  <MenuItem value={OTHER_OPTION}>Other…</MenuItem>
                </Select>
              </FormControl>
              {unitChoice === OTHER_OPTION && (
                <TextField
                  label="Enter your unit"
                  fullWidth
                  required
                  variant="outlined"
                  size={CONTROL_SIZE}
                  sx={controlSx}
                  value={unitOther}
                  onChange={(e) => setUnitOther(e.target.value)}
                  inputProps={{ 'data-testid': 'signup-unit-other' }}
                />
              )}

              {/* Role (state/payload field remains `category`) */}
              <FormControl
                fullWidth
                required
                variant="outlined"
                size={CONTROL_SIZE}
                sx={controlSx}
              >
                <InputLabel id="signup-category-label">Role</InputLabel>
                <Select
                  labelId="signup-category-label"
                  label="Role"
                  size={CONTROL_SIZE}
                  value={category}
                  onChange={(e) => setCategory(e.target.value as string)}
                  data-testid="signup-category-select"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  This helps tailor your Waypoint experience.
                </FormHelperText>
              </FormControl>

              {/* How did you hear about us? */}
              <FormControl
                fullWidth
                required
                variant="outlined"
                size={CONTROL_SIZE}
                sx={controlSx}
              >
                <InputLabel id="signup-heard-label">
                  How did you hear about us?
                </InputLabel>
                <Select
                  labelId="signup-heard-label"
                  label="How did you hear about us?"
                  size={CONTROL_SIZE}
                  value={heardChoice}
                  onChange={(e) => setHeardChoice(e.target.value as string)}
                  data-testid="signup-heard-select"
                >
                  {HEARD_ABOUT_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {heardChoice === 'Other' && (
                <TextField
                  label="Tell us how"
                  fullWidth
                  required
                  variant="outlined"
                  size={CONTROL_SIZE}
                  sx={controlSx}
                  value={heardOther}
                  onChange={(e) => setHeardOther(e.target.value)}
                  inputProps={{ 'data-testid': 'signup-heard-other' }}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {successMessage ? 'Close' : 'Cancel'}
        </Button>
        {!successMessage && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            data-testid="signup-submit"
          >
            Submit request
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SignupForm;
